import * as findItemShapeUtil from './utils/findItemShapeUtil';
import BaseFolderId from 'owa-service/lib/contract/BaseFolderId';
import BasePagingType from 'owa-service/lib/contract/BasePagingType';
import FindItemRequest from 'owa-service/lib/contract/FindItemRequest';
import FindItemResponseMessage from 'owa-service/lib/contract/FindItemResponseMessage';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import SortResults from 'owa-service/lib/contract/SortResults';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import findItemRequest from 'owa-service/lib/factory/findItemRequest';
import findItemOperation from 'owa-service/lib/operation/findItemOperation';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import ItemQueryTraversal from 'owa-service/lib/contract/ItemQueryTraversal';
import FolderId from 'owa-service/lib/contract/FolderId';
import RequestOptions from 'owa-service/lib/RequestOptions';

/**
 * Configure the request body
 * @param currentBaseFolderId the current base folder id
 * @param paging the paging type
 * @param viewFilter the view filter
 * @param sortResults the primary and secondary sort by results
 * @param focusedViewFilter the focused view filter
 * @param findItemShapeName the request shape name
 * @param categoryName the category name
 * @param traversal the traverl to use for this find request
 * @param hashtagsToInclude the hashtags to include for the request
 * @param hashtagsToExclude the hashtags to exclude for the request
 * @param searchFolderId the search folder id
 * @return FindItem request
 */
function configureRequestBody(
    currentBaseFolderId: BaseFolderId,
    paging: BasePagingType,
    viewFilter: ViewFilter,
    sortResults: SortResults[],
    focusedViewFilter: FocusedViewFilter,
    findItemShapeName: string,
    categoryName: string,
    traversal: ItemQueryTraversal,
    hashtagsToInclude?: string[],
    hashtagsToExclude?: string[],
    searchFolderId?: BaseFolderId
): FindItemRequest {
    const folderId = currentBaseFolderId as FolderId;
    const requestData: FindItemRequest = {
        ParentFolderIds: [currentBaseFolderId],
        ItemShape: findItemShapeUtil.getFindItemResponseShape(folderId.Id),
        ShapeName: findItemShapeName,
        Traversal: traversal,
        Paging: paging,
        FocusedViewFilter: focusedViewFilter,
        ViewFilter: viewFilter,
        SortOrder: sortResults,
        CategoryFilter: categoryName,
    };

    // add optional request parameter
    if (searchFolderId) {
        requestData.SearchFolderId = searchFolderId;
    }

    if (hashtagsToInclude && hashtagsToInclude.length > 0) {
        requestData.HashtagsToInclude = hashtagsToInclude;
    }

    if (hashtagsToExclude && hashtagsToExclude.length > 0) {
        requestData.HashtagsToExclude = hashtagsToExclude;
    }

    return findItemRequest(requestData);
}

/**
 * Find item request
 * @param baseFolderId the base folder id
 * @param paging the paging type
 * @param viewFilter the view filter
 * @param sortResults the primary and secondary sort by results
 * @param focusedViewFilter the focused view filter
 * @param findItemShapeName the request shape name
 * @param options - optional parameters to be passed to request (method and/or headers)
 * @param categoryName the category name
 * @param traversal the traversal to use for this find request
 * @param hashtagsToInclude the hashtags to include for the request
 * @param hashtagsToExclude the hashtags to exclude for the request
 * @param searchFolderId the search folder id
 * @return promise containing FindItem response
 */
export function findItem(
    baseFolderId: BaseFolderId,
    paging: BasePagingType,
    viewFilter: ViewFilter,
    sortResults: SortResults[],
    focusedViewFilter: FocusedViewFilter,
    findItemShapeName: string,
    options: RequestOptions,
    categoryName: string,
    traversal: ItemQueryTraversal,
    hashtagsToInclude?: string[],
    hashtagsToExclude?: string[],
    searchFolderId?: BaseFolderId
): Promise<FindItemResponseMessage> {
    if (categoryName && viewFilter != 'UserCategory') {
        throw new Error('CategoryName should be set only for UserCategory view filter');
    }

    const requestBody = configureRequestBody(
        baseFolderId,
        paging,
        viewFilter,
        sortResults,
        focusedViewFilter,
        findItemShapeName,
        categoryName,
        traversal,
        hashtagsToInclude,
        hashtagsToExclude,
        searchFolderId
    );

    return findItemOperation(
        {
            Header: getJsonRequestHeader(),
            Body: requestBody,
        },
        options
    ).then(response => {
        return response.Body.ResponseMessages.Items[0];
    });
}
